export interface UserNotification {
    id: string;
    text: string;
    createdAt: string;
    read: boolean;
    translationKey?: string;
    translationParams?: Record<string, string>;
}

const STORAGE_KEY = "userNotifications";
const UPDATE_EVENT = "app:user-notifications-updated";
const MAX_NOTIFICATIONS = 20;

const isBrowser = () => typeof window !== "undefined";

export const getUserNotifications = (): UserNotification[] => {
    if (!isBrowser()) {
        return [];
    }

    try {
        const rawValue = window.localStorage.getItem(STORAGE_KEY);
        if (!rawValue) {
            return [];
        }

        const parsed = JSON.parse(rawValue) as UserNotification[];
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed;
    } catch {
        return [];
    }
};

const saveUserNotifications = (notifications: UserNotification[]) => {
    if (!isBrowser()) {
        return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS)));
    window.dispatchEvent(new Event(UPDATE_EVENT));
};

interface AddUserNotificationInput {
    text?: string;
    translationKey?: string;
    translationParams?: Record<string, string>;
}

export const addUserNotification = (input: string | AddUserNotificationInput) => {
    const payload: AddUserNotificationInput =
        typeof input === "string" ? { text: input } : input;

    const trimmedText = payload.text?.trim() ?? "";
    if (!trimmedText && !payload.translationKey) {
        return;
    }

    const notifications = getUserNotifications();
    const next: UserNotification[] = [
        {
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            text: trimmedText,
            createdAt: new Date().toISOString(),
            read: false,
            translationKey: payload.translationKey,
            translationParams: payload.translationParams,
        },
        ...notifications,
    ];

    saveUserNotifications(next);
};

export const markUserNotificationAsRead = (notificationId: string) => {
    setUserNotificationReadState(notificationId, true);
};

export const setUserNotificationReadState = (notificationId: string, read: boolean) => {
    const notifications = getUserNotifications();
    const updated = notifications.map((notification) =>
        notification.id === notificationId
            ? { ...notification, read }
            : notification
    );

    saveUserNotifications(updated);
};

export const deleteUserNotification = (notificationId: string) => {
    const notifications = getUserNotifications();
    const updated = notifications.filter((notification) => notification.id !== notificationId);
    saveUserNotifications(updated);
};

export const userNotificationsEvents = {
    UPDATE_EVENT,
};
